/** @jsx jsx */
import { jsx } from '@emotion/core';

import {
  Alert,
  AlertDescription,
  Checkbox,
  Grid,
  SkeletonLoader,
  SkeletonText,
  Spacer,
  Text,
} from '@zapier/design-system';

import Link from 'app/common/components/Link';
import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { useDebouncedValue, usePartnerProfile } from 'app/developer-v3/hooks';

import Memberships from './Memberships';

import type { PartnerProfile } from 'app/developer-v3/types/partnerProfile';

function Container() {
  const {
    isLoading,
    partnerProfile,
    updatePartnerProfile,
  } = usePartnerProfile();
  const [isLoadingDebounced] = useDebouncedValue(isLoading, 1500);

  const onToggleMembership = async (
    membershipToggled: PartnerProfile['memberships'][0]
  ) => {
    const membershipsNext = partnerProfile.memberships.map(membership =>
      membership.app.id === membershipToggled?.app.id &&
      membership.app.type === membershipToggled?.app.type
        ? { ...membershipToggled, optedIn: !membershipToggled.optedIn }
        : membership
    );
    const partnerProfileNext = {
      ...partnerProfile,
      memberships: membershipsNext,
    };
    return updatePartnerProfile(partnerProfileNext);
  };

  const onClickAllMembership = async () => {
    const { memberships } = partnerProfile;
    const isEveryMembershipOff = memberships.every(({ optedIn }) => !optedIn);

    const membershipsNext = memberships.map(membership => ({
      ...membership,
      optedIn: isEveryMembershipOff,
    }));
    const partnerProfileNext = {
      ...partnerProfile,
      memberships: membershipsNext,
    };
    return updatePartnerProfile(partnerProfileNext);
  };

  return (
    <section>
      <Alert status="info">
        <AlertDescription>
          Looking to manage email settings for your primary Zapier account?{' '}
          <Link href={`${ZAPIER_APP_BASE_URL}/app/settings/notifications`}>
            Go to your user email settings
          </Link>
        </AlertDescription>
      </Alert>
      <Spacer height={15} />
      <Text margin="15px 0px" tag="h1" type="SectionHeader">
        Subscribe to updates about your integrations
      </Text>
      <Text margin="15px 0px">
        Get tips and recommendations about your integrations, including our
        monthly newsletter with your performance reports and the latest Zapier
        news.
      </Text>
      <Spacer height={15} />
      {isLoadingDebounced ? (
        <SkeletonLoader>
          <SkeletonText lines={2} size="heading" width="80%" />
        </SkeletonLoader>
      ) : (
        <Memberships
          memberships={partnerProfile.memberships}
          onClickAllMembership={onClickAllMembership}
          onToggleMembership={onToggleMembership}
        />
      )}
      <Spacer height="24px" />
      <Grid columns="max-content auto" gap="0 10px">
        <Checkbox
          isDisabled={true}
          isSelected={true}
          onChange={() => {
            /** This won't be toggleable */
          }}
        />
        <Text tag="h3">Zapier Platform and Partner Program Updates</Text>
        <span />
        <Text tag="p">
          Important announcements regarding Partner Program and Zapier Platform
          features, product updates, and adherence to integration quality
          standards. It is not possible to unsubscribe from this category. These
          messages contain critical information for continued use of the Zapier
          Platform.
        </Text>
      </Grid>
    </section>
  );
}

export default Container;
